import dayjs, {Dayjs} from "dayjs";
import isEmpty from "lodash/isEmpty";
import {debounce, forEach, get, isNumber, split} from "lodash";
import type z from "zod";
import {UseFormSetValue} from "react-hook-form";
import {RoleUserVO} from "@/service/user.s";
import {DependencyList, useEffect} from "react";
import {PaginationVO} from "@/model/global";
import {DictPO} from "@/model/system";
import {ModeType} from "@/store/global.c";
import {ZodErrorMap} from "zod";

type FormEntries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

export const EMPTY_FUNC = () => {
};

export const randomInt = (min: number, max: number): number => Math.floor(Math.random() * (max - min)) + min

export const randomArrayItem = <T>(arr: T[]) => arr[randomInt(0, arr.length - 1)];

export const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * 格式化时间
 * @param type 类型
 * @param time 时间
 * @returns
 */
export const formatTime = (
    type: string,
    time: Date | string | undefined | number | Dayjs,
): string => {
    // if (!(time instanceof Date) || isEmpty(time)) return "";
    if (isEmpty(time)) return '-'
    switch (type) {
        case "Y":
            return dayjs(time).format("YYYY");
        case "M":
            return dayjs(time).format("YYYY-MM");
        case "D":
            return dayjs(time).format("YYYY-MM-DD");
        case "h":
            return dayjs(time).format("YYYY-MM-DD HH");
        case "d":
            return dayjs(time).format("MM/DD HH:mm");
        case "dd":
            return dayjs(time).format("YYYY/MM/DD");
        case "m":
            return dayjs(time).format("YYYY/MM/DD HH:mm");
        case "s":
            return dayjs(time).format("YYYY-MM-DD HH:mm:ss");
        case "MD":
            return dayjs(time).format("MM月DD日");
        case "YM":
            return dayjs(time).format("YY/MM");
        default:
            return "";
    }
};

export const findCurNode = (tree: any[], curKey: number | string, keyField: string, node = null) => {
    tree.forEach((item) => {
        if (item[keyField] === curKey) {
            node = item
        }
        if (item.children && item.children.length) {
            const findChildren = findCurNode(item.children, curKey, keyField, node)
            if (findChildren) {
                node = findChildren
            }
        }
    })
    return node
}

export const transformMoney = (num: any, point = 0): string => {
    // 将数字转换为字符串,然后通过split方法用.分隔,取到第0个
    let numStr = num.toString().split('.')[0]
    if (numStr.length < 6) { // 判断数字有多长,如果小于6,,表示10万以内的数字,让其直接显示
        console.log(numStr);
        return parseFloat(numStr) + '元';
    } else if (numStr.length >= 6 && numStr.length <= 8) { // 如果数字大于6位,小于8位,让其数字后面加单位万
        let decimal = numStr.substring(numStr.length - 4, numStr.length - 4 + point)
        // 由千位,百位组成的一个数字
        return parseFloat(parseInt((num / 10000).toString()) + '.' + decimal) + '万'
    } else if (numStr.length > 8) { // 如果数字大于8位,让其数字后面加单位亿
        let decimal = numStr.substring(numStr.length - 8, numStr.length - 8 + point);
        return parseFloat(parseInt((num / 100000000).toString()) + '.' + decimal) + '亿'
    }
    return ''
}


export const mergeData = <T>(oldDatas: T[], datas: PaginationVO<T> = {} as PaginationVO<T>) => {
    if (isEmpty(datas.list) || datas.list.length < 1) {
        datas.list = [];
    }
    let newDatas = oldDatas;
    let hasMore = false;

    if (datas.current === 1) {
        newDatas = [];
    }

    newDatas = newDatas.concat(datas.list);
    hasMore = datas.current < Math.ceil(datas.total / datas.size);
    return {...datas, list: newDatas, hasMore};
};

export const getSellerNames = (creator?: string, seller?: string, flag = 1): string | string[] => {
    let names = []
    if (creator === seller) {
        if (creator) {
            names.push(creator);
        }
    } else {
        if (creator) {
            names.push(creator);
        }
        if (seller) {
            names.push(seller);
        }
    }
    if (flag == 2) {
        return names
    }
    return names?.join(",") || '-';
}

export const str2ArrBuff = (str: string) => {
    const buf = new ArrayBuffer(str.length); // 2 bytes for each char
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
        bufView[i] = str.charCodeAt(i);
    }
    return buf;
}

export const useAsyncEffect = (
    effect: () => Promise<any>,
    deps?: DependencyList
) => {
    useEffect(() => {
        effect();
    }, deps);
};

export const exportFile = (data: Blob, fileName: string) => {
    const blob = new Blob([data]);
    if (!blob) {
        console.warn("No blob data provided for download.");
        return;
    }
    const link = document.createElement('a');
    const href = URL.createObjectURL(blob);
    link.href = href;
    link.download = fileName
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
};

export const stringAvatar = (name: string) => {
    function stringToColor(string: string) {
        let hash = 0;
        let i;

        /* eslint-disable no-bitwise */
        for (i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = '#';

        for (i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        /* eslint-enable no-bitwise */

        return color;
    }

    return {
        sx: {
            width: 35, height: 34, fontSize: 10, border: 'none',
            bgcolor: stringToColor(name),
        },
        children: name,
    };
}

export const checkFileType = (mediaTypes: string[], fileType: string): boolean => {
    return mediaTypes.indexOf(fileType?.toLowerCase()) > -1
}

// 获取数组最后一个元素
export const getLastStr = (str: string | undefined, defaultValue = '-') => {
    if (!isEmpty(str)) {
        const arr = (str && str.split(",")) || [];
        return get(arr.splice(-1), "[0]", "");
    }
    return defaultValue;
};

/**
 * 生成随机字符串
 * @param len 长度
 * @returns 字符串
 */
export const randomString = (len: number): string => {
    const str = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let result = "";
    for (let i = len; i > 0; --i)
        result += str[Math.floor(Math.random() * str.length)];
    return result;
};

export const callDebounce = debounce((values: any, func: (params: any) => void) => {
    return func(values);
}, 1000);

export const str2arr = (
    str?: string,
    type = "number",
    symbol = ","
): string[] | number[] | any => {
    return (
        (str &&
            split(str, symbol).map((item: string) =>
                type == "number" ? parseInt(item) : item.toString()
            )) ||
        []
    );
};

export function emptyValue<T extends z.ZodFirstPartySchemaTypes>(
    schema: T,
): z.input<T> {
    const def = schema._def;
    if (
        !("coerce" in def && def.coerce) &&
        schema.isNullable() &&
        def.typeName !== "ZodDefault"
    ) {
        return '';
    }

    switch (def.typeName) {
        case "ZodObject": {
            const outputObject: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(def.shape())) {
                outputObject[key] = emptyValue(value as z.ZodFirstPartySchemaTypes);
            }
            return outputObject;
        }
        case "ZodRecord":
            return {};
        case "ZodString": {
            // if (def.checks) {
            //     for (const check of def.checks) {
            //         if (check.kind === "uuid") {
            //             return randomString(8);
            //         }
            //     }
            // }
            return '';
        }
        case "ZodNumber":
            // for (const check of def.checks || []) {
            //     if (check.kind === "max" || check.kind === "min") {
            //         return check.value;
            //     }
            // }
            return '';
        case "ZodBigInt":
            return BigInt(0);
        case "ZodBoolean":
            return false;
        case "ZodDate":
            return null;
        case "ZodLiteral":
            return def.value;
        case "ZodEffects":
            return emptyValue(def.schema);
        case "ZodArray":
            return [];
        case "ZodTuple":
            return def.items.map((item: z.ZodTypeAny) => emptyValue(item));
        case "ZodSet":
            return new Set();
        case "ZodMap":
            return new Map();
        case "ZodEnum":
            return def.values[0];
        case "ZodNativeEnum":
            return Object.values(def.values).filter(
                (value) => typeof def.values[value as any] !== "number",
            )[0];
        case "ZodUnion":
            return emptyValue(def.options[0]);
        case "ZodDiscriminatedUnion":
            return emptyValue(Array.from(def.options.values() as any[])[0]);
        case "ZodIntersection":
            return Object.assign(emptyValue(def.left) as any, emptyValue(def.right));
        case "ZodFunction":
            return (..._: any[]) => emptyValue(def.returns);
        case "ZodLazy":
            return emptyValue(def.getter());
        case "ZodPipeline":
            return emptyValue(def.in);
        case "ZodDefault":
            return def.defaultValue();
        case "ZodNaN":
            return Number.NaN;
        case "ZodNull":
        case "ZodAny":
            return null;
        case "ZodOptional":
            return emptyValue(def.innerType);
        // case "ZodUndefined":
        // case "ZodVoid":
        // case "ZodUnknown":
        // case "ZodNever":
        default:
            return undefined;
    }
}

export const setValues = <T>(formValue: T, setValue: UseFormSetValue<any>) => {
    for (const [key, value] of Object.entries(formValue as FormEntries<T>)) {
        setValue(key as string, value);
    }
}

export const getSelectValues = <T>(options: T[], attr: { key: string, val: string }, res: { key: string, val: string }) => {
    const values: number[] = []
    const labels: string[] = []
    forEach(options, (item: any) => {
        values.push(item[attr['key']])
        labels.push(item[attr['val']])
    })
    return {[res['key']]: labels.join(","), [res['val']]: values.join(",")};
}

export const filterUsers = (allUser: RoleUserVO[], userIds: string): RoleUserVO[] => {
    return allUser.filter((user: RoleUserVO) => {
        const ids = str2arr(userIds)
        return ids.includes(user.id)
    }) || []
}

export const filterDicts = (allDict: DictPO[], values?: string): DictPO[] => {
    if (isEmpty(values)) {
        return []
    }
    return allDict.filter((dict: DictPO) => {
        const ids = str2arr(values)
        return ids.includes(dict.value)
    }) || []
}

export const filterUser = (allUser: RoleUserVO[], userId: number): RoleUserVO => {
    return allUser.find((user: RoleUserVO) => {
        return user.id == userId
    }) as RoleUserVO
}

export const errDateMap = (message: string): ZodErrorMap => {
    return ({code}: { code: string }, {defaultError}: { defaultError: string }) => {
        if (code == 'invalid_date') return {message}
        return {message: defaultError}
    }
}

export const date2strTime = (date: any): any => {
    if (date) {
        return dayjs(date).format()
    }
    return null
}

export const iso2Date = (str?: string | Date) => {
    return str && new Date(str) || null
}

export const getRealId = (mode?: ModeType, drawerId?: number, dialogId?: number): number => {
    if (mode == 'dialog') {
        return dialogId!;
    } else if (mode == 'drawer') {
        return drawerId!;
    }
    return 0
}

export const getRealWrite = (mode?: ModeType, drawerWrite?: string, dialogWrite?: string): string => {
    if (mode == 'dialog') {
        return dialogWrite!;
    } else if (mode == 'drawer') {
        return drawerWrite!;
    }
    return ""
}

export const getRealSearch = (mode?: ModeType, drawerSearch?: () => void, dialogSearch?: () => void): (() => void) | null => {
    if (mode == 'dialog') {
        return dialogSearch!;
    } else if (mode == 'drawer') {
        return drawerSearch!;
    }
    return null
}

export const formatMoney = (money?: number): string => {
    return money && String(money)
        .split('')
        .reverse()
        .reduce((prev, next, index) => {
            return (index % 3 ? next : next + ',') + prev;
        }) || "";
}

export const checkQueryKey = (num: any) => isNumber(num) && num > 0